import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { every, filter, isEmpty, last, map } from 'es-toolkit/compat';
import React from 'react';

import Loader, { LoadingStage } from 'components/Loader';
import { AgentStreamAction, AgentStreamProgress, FollowUpOption, TokenTrace } from 'interfaces/agentSession';
import { AgentActionTimeline } from './AgentActionTimeline';
import { AssistantMessageContent } from './AssistantMessageContent';
import { getRenderableAssistantContent, sanitizeAssistantResponseContent } from './helpers';
import { AgentProgressIndicator, AgentStatusIndicator, LoadingIndicator } from './Loading';
import { MessageAvatar } from './MessageAvatar';
import { TokenTracePopup } from './TokenTracePopup';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  isError?: boolean;
  tokenTrace?: TokenTrace | null;
  followUpOptions?: FollowUpOption[];
}

interface Props {
  messages: ChatMessage[];
  isLoading?: boolean;
  onRetry?: (userMessage: string) => void;
  agentStatus?: string | null;
  agentProgress?: AgentStreamProgress | null;
  agentActions?: AgentStreamAction[];
  canViewTokenUsageDebug?: boolean;
  tokenTraces?: TokenTrace[];
  bottomAnchorRef?: React.RefObject<HTMLDivElement | null>;
  onUsePromptSuggestion?: (prompt: string) => void;
  onSendPromptSuggestion?: (prompt: string) => void;
  promptSuggestionsDisabled?: boolean;
  studyId?: string;
}

const getStreamingMessageLabel = ({
  agentStatus,
  agentProgress,
  agentActions,
}: {
  agentStatus?: string | null;
  agentProgress?: AgentStreamProgress | null;
  agentActions?: AgentStreamAction[];
}) => {
  if (agentProgress?.text) {
    return agentProgress.text;
  }

  const runningActions = filter(agentActions, (action) => action.status === 'running');
  if (runningActions.length === 1) {
    return runningActions[0].label;
  }
  if (runningActions.length > 1) {
    return `${runningActions.length} actions running in parallel`;
  }

  if (agentStatus) {
    return agentStatus;
  }

  return 'Thinking';
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const UserMessageContent: React.FC<{ content: string }> = ({ content }) => (
  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
    {content}
  </Typography>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CopilotConversation: React.FC<React.PropsWithChildren<Props>> = ({
  messages,
  isLoading,
  onRetry,
  agentStatus,
  agentProgress,
  agentActions = [],
  canViewTokenUsageDebug = false,
  tokenTraces = [],
  bottomAnchorRef,
  onUsePromptSuggestion,
  onSendPromptSuggestion,
  promptSuggestionsDisabled = false,
  studyId,
}) => {
  const theme = useTheme();
  const internalBottomAnchorRef = React.useRef<HTMLDivElement>(null);
  const resolvedBottomAnchorRef = bottomAnchorRef || internalBottomAnchorRef;
  const assistantMessages = filter(messages, (message) => message.role === 'assistant');
  const latestAssistantMessage = last(assistantMessages);
  const hasStreamingAssistantMessage = Boolean(last(messages)?.role === 'assistant' && last(messages)?.isStreaming);
  const latestAssistantMessageId = latestAssistantMessage?.id;
  const latestAssistantVisibleContent = sanitizeAssistantResponseContent(latestAssistantMessage?.content || '');
  const hasOnlyCompletedAgentActions =
    !isEmpty(agentActions) && every(agentActions, (action) => action.status === 'completed');
  const latestSessionLevelTokenTrace =
    tokenTraces.length > filter(assistantMessages, (message) => Boolean(message.tokenTrace)).length
      ? last(tokenTraces) || null
      : null;
  const latestAgentFlowTokenTrace = latestAssistantMessage?.tokenTrace || latestSessionLevelTokenTrace;
  const shouldShowAgentFlow =
    (!hasStreamingAssistantMessage || !hasOnlyCompletedAgentActions || !latestAssistantVisibleContent) &&
    (!isEmpty(agentActions) ||
      (hasStreamingAssistantMessage && !latestAssistantVisibleContent && Boolean(latestAgentFlowTokenTrace?.planText)));
  const streamingMessageLabel = getStreamingMessageLabel({ agentStatus, agentProgress, agentActions });

  if (isEmpty(messages)) {
    if (isLoading) {
      return (
        <>
          <Loader stage={LoadingStage.LoadingConversation} />
          <div ref={resolvedBottomAnchorRef} />
        </>
      );
    }

    return null;
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {map(messages, (msg, index) => {
        const isAssistantMessage = msg.role === 'assistant';
        const avatarBackgroundColor = isAssistantMessage ? theme.palette.primary.main : theme.palette.grey[400];
        const currentTokenTrace =
          msg.tokenTrace || (msg.id === latestAssistantMessageId ? latestSessionLevelTokenTrace : null);
        let renderableAssistantContent = null;

        if (isAssistantMessage) {
          renderableAssistantContent = getRenderableAssistantContent({
            content: msg.content,
            isStreaming: msg.isStreaming,
            isLatestAssistantMessage: msg.id === latestAssistantMessageId,
            agentStatus,
            agentProgress,
            agentActions,
          });
        }

        return (
          <Stack key={msg.id} direction="row" spacing={1.5} alignItems="flex-start">
            <MessageAvatar role={msg.role} bgcolor={avatarBackgroundColor} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                {isAssistantMessage ? 'Assistant' : 'You'}
              </Typography>
              {isAssistantMessage && renderableAssistantContent ? (
                <>
                  <AssistantMessageContent
                    content={renderableAssistantContent.content}
                    isStreaming={msg.isStreaming}
                    showStreamingIndicator={renderableAssistantContent.showStreamingIndicator}
                    streamingLabel={msg.isStreaming ? streamingMessageLabel : undefined}
                    followUpOptions={msg.followUpOptions}
                    onUsePromptSuggestion={onUsePromptSuggestion}
                    onSendPromptSuggestion={onSendPromptSuggestion}
                    promptSuggestionsDisabled={promptSuggestionsDisabled}
                    studyId={studyId}
                  />
                  <TokenTracePopup
                    canViewTokenUsageDebug={canViewTokenUsageDebug}
                    tokenTraces={
                      msg.tokenTrace
                        ? [msg.tokenTrace]
                        : msg.id === latestAssistantMessageId && currentTokenTrace
                        ? [currentTokenTrace as TokenTrace]
                        : []
                    }
                  />
                </>
              ) : (
                <UserMessageContent content={msg.content} />
              )}
              {msg.isError &&
                onRetry &&
                (() => {
                  const precedingUserMsg = messages[index - 1];
                  const userText = precedingUserMsg?.role === 'user' ? precedingUserMsg.content : undefined;
                  return userText ? (
                    <Button
                      size="small"
                      startIcon={<ReplayRoundedIcon fontSize="small" />}
                      onClick={() => onRetry(userText)}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Try again
                    </Button>
                  ) : null;
                })()}
            </Box>
          </Stack>
        );
      })}
      {shouldShowAgentFlow ? (
        <AgentActionTimeline
          actions={agentActions}
          currentStatus={agentStatus}
          currentProgress={agentProgress}
          isStreaming={hasStreamingAssistantMessage}
          planText={latestAgentFlowTokenTrace?.planText}
        />
      ) : null}
      {agentProgress ? <AgentProgressIndicator progressEvent={agentProgress} /> : null}
      {isEmpty(agentActions) && !agentProgress && agentStatus ? <AgentStatusIndicator text={agentStatus} /> : null}
      {isLoading && last(messages)?.role !== 'assistant' ? <LoadingIndicator /> : null}
      <div ref={resolvedBottomAnchorRef} />
    </Stack>
  );
};
